/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import {
  LEISURE_AREAS, FACILITIES, SECURITY_ITEMS,
} from './schema.constants';

type CategoryConfig = {
  characteristics: ('bedrooms' | 'suites' | 'bathrooms' | 'parking_spots')[];
  leisure: readonly string[];
  facilities: readonly string[];
  security: readonly string[];
};

const RURAL_LEISURE = ['Piscina', 'Quadra_poliesportiva', 'Churrasqueira', 'Jardim', 'Espaco_gourmet'] as const;
const RURAL_FACILITIES = ['Area_de_servico', 'Lavanderia', 'Deposito'] as const;
const RURAL_SECURITY = ['Cameras_de_seguranca', 'Alarme', 'Cerca_eletrica'] as const;

const COMMERCIAL_FACILITIES = ['Elevador', 'Ar_condicionado', 'Area_de_servico', 'Deposito', 'Coworking', 'Bicicletario'] as const;
const OFFICE_FACILITIES = ['Elevador', 'Ar_condicionado', 'Coworking', 'Bicicletario'] as const;
const WAREHOUSE_FACILITIES = ['Ar_condicionado', 'Deposito'] as const;

const TERRAIN_SECURITY = ['Cameras_de_seguranca', 'Alarme', 'Cerca_eletrica'] as const;
const GARAGE_SECURITY = ['Seguranca_24h', 'Cameras_de_seguranca', 'Alarme'] as const;

const ALL_CHARS: CategoryConfig['characteristics'] = ['bedrooms', 'suites', 'bathrooms', 'parking_spots'];
const COMMERCIAL_CHARS: CategoryConfig['characteristics'] = ['bathrooms', 'parking_spots'];

const CATEGORY_CONFIG: Record<string, CategoryConfig> = {
  'Casa': { characteristics: ALL_CHARS, leisure: LEISURE_AREAS, facilities: FACILITIES, security: SECURITY_ITEMS },
  'Apartamento': { characteristics: ALL_CHARS, leisure: LEISURE_AREAS, facilities: FACILITIES, security: SECURITY_ITEMS },
  'Kitnet': { characteristics: ALL_CHARS, leisure: LEISURE_AREAS, facilities: FACILITIES, security: SECURITY_ITEMS },
  'Studio': { characteristics: ALL_CHARS, leisure: LEISURE_AREAS, facilities: FACILITIES, security: SECURITY_ITEMS },
  'Flat': { characteristics: ALL_CHARS, leisure: LEISURE_AREAS, facilities: FACILITIES, security: SECURITY_ITEMS },
  'Loft': { characteristics: ALL_CHARS, leisure: LEISURE_AREAS, facilities: FACILITIES, security: SECURITY_ITEMS },
  'Cobertura': { characteristics: ALL_CHARS, leisure: LEISURE_AREAS, facilities: FACILITIES, security: SECURITY_ITEMS },
  'Chacara': { characteristics: ALL_CHARS, leisure: RURAL_LEISURE, facilities: RURAL_FACILITIES, security: RURAL_SECURITY },
  'Fazenda': { characteristics: ALL_CHARS, leisure: RURAL_LEISURE, facilities: RURAL_FACILITIES, security: RURAL_SECURITY },
  'Sitio': { characteristics: ALL_CHARS, leisure: RURAL_LEISURE, facilities: RURAL_FACILITIES, security: RURAL_SECURITY },
  'Predio Comercial': { characteristics: COMMERCIAL_CHARS, leisure: [], facilities: COMMERCIAL_FACILITIES, security: SECURITY_ITEMS },
  'Casa_Comercial': { characteristics: COMMERCIAL_CHARS, leisure: [], facilities: COMMERCIAL_FACILITIES, security: SECURITY_ITEMS },
  'Sala Comercial': { characteristics: COMMERCIAL_CHARS, leisure: [], facilities: OFFICE_FACILITIES, security: SECURITY_ITEMS },
  'Ponto_Comercial': { characteristics: COMMERCIAL_CHARS, leisure: [], facilities: OFFICE_FACILITIES, security: SECURITY_ITEMS },
  Galpao: { characteristics: COMMERCIAL_CHARS, leisure: [], facilities: WAREHOUSE_FACILITIES, security: SECURITY_ITEMS },
  Academia: { characteristics: COMMERCIAL_CHARS, leisure: [], facilities: COMMERCIAL_FACILITIES, security: SECURITY_ITEMS },
  'Edifício': { characteristics: COMMERCIAL_CHARS, leisure: [], facilities: COMMERCIAL_FACILITIES, security: SECURITY_ITEMS },
  Terreno: { characteristics: [], leisure: [], facilities: [], security: TERRAIN_SECURITY },
  Garagem: { characteristics: [], leisure: [], facilities: [], security: GARAGE_SECURITY },
};

const DEFAULT_CONFIG: CategoryConfig = { characteristics: ALL_CHARS, leisure: LEISURE_AREAS, facilities: FACILITIES, security: SECURITY_ITEMS };

/**
 * Resolves the field configuration (characteristics, leisure, facilities,
 * security) for a property category, falling back to the residential default
 * when the category is unknown.
 *
 * @param category - Property category key.
 * @returns The matching category configuration, or the default configuration.
 */
export function getCategoryConfig(category: string): CategoryConfig {
  return CATEGORY_CONFIG[category] || DEFAULT_CONFIG;
}
